import { serviceQueryKeys } from "../provider/admin/service-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/lib/use-toast";

import type {
  FormDetail,
  FormTable,
  PatientSubmissionsTable,
} from "@/types/forms/form";
import type { NoteTemplateTableItem } from "@/types/notes";

import {
  ConvertFormToTemplateRequest,
  MergeFormsRequest,
  ReplaceFormsRequest,
  archiveForm,
  archiveSubmission,
  bulkArchiveForms,
  convertFormToNoteTemplate,
  createForm,
  mergeForms,
  refillForms,
  removeReplacedForm,
  replaceForms,
  updateFormExpiryDays,
} from "./form-management";
import { formQueryKeys } from "./form-query";
import { eventQueryKeys } from "@/query/check-in/calendar/event-query";

/** `tableKey` is the list the archive is made from; it defaults to the clinical forms list. */
export const useArchiveFormMutation = (
  tableKey: readonly unknown[] = formQueryKeys.table(false),
) => {
  const queryClient = useQueryClient();

  return useMutation<
    boolean | null,
    Error,
    string,
    { previousData: FormTable[] | undefined }
  >({
    mutationFn: (formId: string) => archiveForm(formId),
    onMutate: async (formId: string) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: tableKey,
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<FormTable[]>(tableKey);

      // Optimistically update the cache by removing the archived form
      queryClient.setQueryData<FormTable[]>(
        tableKey,
        (old) => old?.filter((form) => form.id !== formId) ?? [],
      );

      return { previousData };
    },
    onError: (_error, _formId, context) => {
      // Rollback to the previous value on error
      if (context?.previousData) {
        queryClient.setQueryData(tableKey, context.previousData);
      }
      toast({
        title: "Error",
        description: "Failed to archive form. Please try again.",
        variant: "error",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Form archived successfully.",
        variant: "success",
      });
    },
    onSettled: () => {
      // Invalidate to refetch and ensure consistency
      queryClient.invalidateQueries({
        queryKey: [...formQueryKeys.all, "table"],
      });
    },
  });
};

export const useArchiveSubmissionMutation = (patientId: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    boolean | null,
    Error,
    string,
    { previousData: PatientSubmissionsTable | undefined }
  >({
    mutationFn: (answeredFormId: string) => archiveSubmission(answeredFormId),
    onMutate: async (answeredFormId: string) => {
      await queryClient.cancelQueries({
        queryKey: formQueryKeys.submissions(patientId),
      });

      const previousData = queryClient.getQueryData<PatientSubmissionsTable>(
        formQueryKeys.submissions(patientId),
      );

      queryClient.setQueryData<PatientSubmissionsTable>(
        formQueryKeys.submissions(patientId),
        (old) =>
          old
            ? {
                ...old,
                data: old.data.filter(
                  (sub) => sub.answeredFormId !== answeredFormId,
                ),
                total: Math.max(0, old.total - 1),
              }
            : old,
      );

      return { previousData };
    },
    onError: (_error, _answeredFormId, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          formQueryKeys.submissions(patientId),
          context.previousData,
        );
      }
      toast({
        title: "Error",
        description: "Failed to archive submission. Please try again.",
        variant: "error",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Submission archived. The form can be sent again.",
        variant: "success",
      });
    },
  });
};

/** Asks the patient to fill forms again; see `refillForms`. */
export const useRefillFormsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      patientId: string;
      formIds: string[];
      eventId?: string;
    }) => refillForms(payload),
    onSuccess: async (result, { patientId, eventId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: formQueryKeys.submissions(patientId),
        }),
        // Coverage by older forms is checked per patient; any of those lists
        // may now be out of date.
        queryClient.invalidateQueries({
          queryKey: [...formQueryKeys.all, "statuses", patientId],
        }),
        queryClient.invalidateQueries({
          queryKey: [...formQueryKeys.all, "status"],
        }),
        ...[
          ...new Set([
            ...(eventId ? [eventId] : []),
            ...(result?.reopenedEventIds ?? []),
          ]),
        ].map((id) =>
          queryClient.invalidateQueries({
            queryKey: eventQueryKeys.appointmentChecklist(id),
          }),
        ),
      ]);
    },
    onError: (error) => {
      toast({
        title: "Couldn't set up the form to fill again",
        description: error.message || "Please try again.",
        variant: "error",
      });
    },
  });
};

export const useBulkArchiveFormsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    boolean | null,
    Error,
    string[],
    { previousData: FormTable[] | undefined }
  >({
    mutationFn: (ids: string[]) => bulkArchiveForms(ids),
    onMutate: async (ids: string[]) => {
      await queryClient.cancelQueries({
        queryKey: formQueryKeys.table(false),
      });

      const previousData = queryClient.getQueryData<FormTable[]>(
        formQueryKeys.table(false),
      );

      queryClient.setQueryData<FormTable[]>(
        formQueryKeys.table(false),
        (old) => old?.filter((form) => !ids.includes(form.id)) ?? [],
      );

      return { previousData };
    },
    onError: (_error, _ids, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          formQueryKeys.table(false),
          context.previousData,
        );
      }
      toast({
        title: "Error",
        description: "Failed to archive forms. Please try again.",
        variant: "error",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Forms archived successfully.",
        variant: "success",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...formQueryKeys.all, "table"],
      });
    },
  });
};

export const useCreateFormMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<FormDetail | null, Error, FormDetail>({
    mutationFn: (formData: FormDetail) => createForm(formData),
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Form created successfully.",
        variant: "success",
      });
      // Invalidate all form table queries (both with and without archived)
      queryClient.invalidateQueries({
        queryKey: [...formQueryKeys.all, "table"],
      });
      // Also invalidate list query if it exists
      queryClient.invalidateQueries({ queryKey: formQueryKeys.list() });
    },
    onError: () => {
      console.error("Failed to create form");
      toast({
        title: "Error",
        description: "Failed to create form. Please try again.",
        variant: "error",
      });
    },
  });
};

export const useUpdateFormExpiryDaysMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    boolean | null,
    Error,
    { formId: string; expiryDays: number | null },
    { previousData: FormTable[] | undefined }
  >({
    mutationFn: ({ formId, expiryDays }) =>
      updateFormExpiryDays(formId, expiryDays),
    onMutate: async ({ formId, expiryDays }) => {
      await queryClient.cancelQueries({
        queryKey: formQueryKeys.table(false),
      });

      const previousData = queryClient.getQueryData<FormTable[]>(
        formQueryKeys.table(false),
      );

      queryClient.setQueryData<FormTable[]>(
        formQueryKeys.table(false),
        (old) =>
          old?.map((form) =>
            form.id === formId ? { ...form, expiryDays } : form,
          ) ?? [],
      );

      return { previousData };
    },
    onError: (_error, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          formQueryKeys.table(false),
          context.previousData,
        );
      }
      toast({
        title: "Error",
        description: "Failed to update expiry days. Please try again.",
        variant: "error",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Expiry days updated successfully.",
        variant: "success",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...formQueryKeys.all, "table"],
      });
    },
  });
};

export const useMergeFormsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    boolean | null,
    Error,
    MergeFormsRequest,
    { previousData: FormTable[] | undefined }
  >({
    mutationFn: (request: MergeFormsRequest) => mergeForms(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...formQueryKeys.all, "table"],
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to merge forms. Please try again.",
        variant: "error",
      });
    },
  });
};

export const useReplaceFormsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: ReplaceFormsRequest) => replaceForms(request),
    onSuccess: () => {
      // Services, form lists and patients' form status all change.
      queryClient.invalidateQueries({ queryKey: formQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: serviceQueryKeys.all });
    },
  });
};

export const useRemoveReplacedFormMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      formId,
      replacedFormId,
    }: {
      formId: string;
      replacedFormId: string;
    }) => removeReplacedForm(formId, replacedFormId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formQueryKeys.all });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Couldn't remove the link. Please try again.",
        variant: "error",
      });
    },
  });
};

export const useConvertFormToTemplateMutation = () => {
  return useMutation<
    NoteTemplateTableItem | null,
    Error,
    { formId: string; options?: ConvertFormToTemplateRequest }
  >({
    mutationFn: ({ formId, options }) =>
      convertFormToNoteTemplate(formId, options),
    // Note: onSuccess/onError handled by the component for custom toast with link
  });
};
